import {
  selectCitiesCache,
  selectCurrentPlayer,
  selectGameType,
  selectGoblinsTrapDeathHexKeys,
  selectGoblinsTraps,
  selectHexes,
  selectPendingManeuverActions,
  selectRound,
  selectTurnOrder,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {
  addPendingManeuverAction,
  applyGoblinsTrapMovement,
  moveUnit,
  showGoblinsTrapKillNotice
} from "../../../redux/actions";
import {calculateAvailableManeuverCells} from "../../gameState/calculateAvailableManeuverCells";
import {checkIfUnitCouldManeuverFromHex} from "../../gameState/maneuver/checkIfUnitCouldManeuverFromHex";
import {getPlayerUnits} from "../../players/getPlayerUnits";
import {getNeighborEnemyUnits} from "../../unit/getNeighborEnemyUnits";
import {getBotGridRenderer} from "../botGrid";
import {getEnemyQuarterHexKeys} from "../evaluation/cities";
import {BOT_WEIGHTS} from "../botConfig";
import {getBotExpectedMeleeDamage} from "../factions/celectialsBot";
import {isPreW32DefensePlayer} from "../abTestGates";
import {CITY_THREAT_LEVELS, getCityCaptureThreats, getPinnedHolders} from "../evaluation/cityCaptureThreat";
import {
  buildGarrisonUnitIndex,
  checkIfW35GarrisonPlayer,
  getStoredGarrisonAssignments,
  isGarrisonLockHard,
} from "../evaluation/garrisons";
import {getBotStrategy} from "../strategy/botStrategyStore";
import {W35_GARRISON_MANEUVER_BONUS} from "../../../configs/botGarrison";
import {resolveMovementWithTraps} from "../../movement/resolveMovementWithTraps";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {keyToCoords} from "../../coords/keyToCoords";

/**
 * Этап маневра фазы войны: один маневр за тик.
 * Приоритет: встать на квартал чужого города (захват), затем отступление из-под угрозы.
 */
export const decideManeuverStep = (store) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const hexes = selectHexes(state);
  const units = selectUnits(state);
  const citiesCache = selectCitiesCache(state);
  const pendingManeuverActions = selectPendingManeuverActions(state);

  const gridRenderer = getBotGridRenderer(state);
  const enemyQuarters = getEnemyQuarterHexKeys(citiesCache, currentPlayer.color);

  // Единая точка правды с GameScene.handleManeuverPhaseClick/HexGridRenderer -
  // раньше здесь дублировался только shot/fought-вариант условия, и бот не
  // мог маневрировать отрядом, участвовавшим в войне через этап способностей
  // (соблазнение/способности Гоблинов) - см. add-goblins-faction design.md
  const maneuverableUnits = getPlayerUnits(currentPlayer, units).filter((unit) => {
    if (pendingManeuverActions.some((action) => action.unitId === unit.id)) return false;
    return checkIfUnitCouldManeuverFromHex(currentPlayer, unit, hexes);
  });

  if (maneuverableUnits.length === 0) {
    return false;
  }

  // Волна 32 (оборона, гейт isPreW32DefensePlayer): единственный держатель
  // кварталов города, который иначе захватят в конце ЭТОГО раунда (враг на
  // квартале или противник, ходящий после бота, дотягивается), не отступает —
  // разве что на другой квартал того же города. У legacy null
  const w32PinnedHolders = isPreW32DefensePlayer(currentPlayer.id)
    ? null
    : getPinnedHolders(getCityCaptureThreats(state, currentPlayer).cityThreats, CITY_THREAT_LEVELS.THIS_ROUND);
  // Волна 35 (G, checkIfW35GarrisonPlayer: бонус «большой» и выше, гейт
  // isPreW35GarrisonPlayer): гарнизон по доктрине (назначение этого раунда из
  // перемещения) с соседней клетки встает на свободный квартал. В режиме 'hard' он
  // маневрирует только по кварталам своего города, в 'soft' уход с кварталов стоит
  // ему ценности держателя города. У прочих ботов и legacy null
  const w35GarrisonByUnitId = checkIfW35GarrisonPlayer(currentPlayer, selectGameType(state))
    ? buildGarrisonUnitIndex(getStoredGarrisonAssignments(getBotStrategy(currentPlayer.id), selectRound(state)))
    : null;
  const isW35GarrisonLockHard = Boolean(w35GarrisonByUnitId) && isGarrisonLockHard();

  let bestManeuver = null;
  let bestScore = 0;

  for (const unit of maneuverableUnits) {
    const availableManeuverCells = calculateAvailableManeuverCells(
      gridRenderer, hexes, units, unit, selectGoblinsTrapDeathHexKeys(state)
    );
    const pinnedCityQuarters = w32PinnedHolders ? w32PinnedHolders.get(unit.id) : null;
    const garrisonCity = w35GarrisonByUnitId ? w35GarrisonByUnitId.get(unit.id) : undefined;

    const currentThreat = getNeighborEnemyUnits(unit.ownerId, unit.hexKey, units)
      .reduce((sum, enemy) => sum + getBotExpectedMeleeDamage(enemy, unit.ownerId), 0);

    for (const cellKey of availableManeuverCells) {
      if (pinnedCityQuarters && !pinnedCityQuarters.includes(cellKey)) {
        continue;
      }
      if (garrisonCity && isW35GarrisonLockHard && !garrisonCity.quarters.has(cellKey)) {
        continue;
      }
      let score = 0;

      if (enemyQuarters.has(cellKey)) {
        score += BOT_WEIGHTS.CAPTURE_ENEMY_QUARTER;
      }

      if (garrisonCity) {
        const isOnGarrisonQuarter = garrisonCity.quarters.has(unit.hexKey);
        const isToGarrisonQuarter = garrisonCity.quarters.has(cellKey);
        // Волна 35 (G): гарнизон, оказавшийся вне кварталов, возвращается на квартал
        if (!isOnGarrisonQuarter && isToGarrisonQuarter) {
          score += W35_GARRISON_MANEUVER_BONUS;
        }
        // Режим 'soft': уход гарнизона с кварталов своего города — минус ценность держателя
        if (isOnGarrisonQuarter && !isToGarrisonQuarter) {
          score -= garrisonCity.holdValue;
        }
      }

      // Отступление: уходим из-под превосходящей угрозы в более безопасную ячейку
      const cellThreat = getNeighborEnemyUnits(unit.ownerId, cellKey, units)
        .reduce((sum, enemy) => sum + getBotExpectedMeleeDamage(enemy, unit.ownerId), 0);
      if (currentThreat >= unit.size && cellThreat < currentThreat) {
        score += BOT_WEIGHTS.MANEUVER_RETREAT;
      }

      if (score > bestScore) {
        bestScore = score;
        bestManeuver = {unit, cellKey};
      }
    }
  }

  if (!bestManeuver) {
    return false;
  }

  // Ловушки Гоблинов должны срабатывать и при манёвре бота, той же единой
  // точкой правды, что и у перемещения (resolveMovementWithTraps, см.
  // decideMovementStep.js и GameScene.handleManeuverPhaseClick,
  // add-goblins-faction design.md - манёвр раньше вообще не прогонялся через
  // резолвер). Манёвр - всегда прямой перескок на один гекс, путь для
  // резолвера строится так же, как в GameScene.js (два узла: старт и цель)
  const stateForTrapCheck = store.getState();
  const goblinsTraps = selectGoblinsTraps(stateForTrapCheck);
  let resolution = null;
  if (goblinsTraps.length > 0) {
    const [startQ, startR] = keyToCoords(bestManeuver.unit.hexKey);
    const [endQ, endR] = keyToCoords(bestManeuver.cellKey);
    const maneuverPath = [{q: startQ, r: startR}, {q: endQ, r: endR}];
    const turnOrder = selectTurnOrder(stateForTrapCheck);
    const candidateResolution = resolveMovementWithTraps(maneuverPath, bestManeuver.unit, goblinsTraps, turnOrder);
    if (candidateResolution.triggeredTrapEvents.length > 0) {
      resolution = candidateResolution;
    }
  }

  if (resolution) {
    // isManeuver: true - reducer пишет в pendingManeuverActions, а не в
    // pendingMovementActions (design.md) - без этого бот на следующем тике
    // выбрал бы этот же выживший отряд для манёвра повторно (тот же класс
    // бага, что уже чинился для перемещения). trapTriggered: true внутри
    // реализует необратимость (Decision 8)
    store.dispatch(applyGoblinsTrapMovement(bestManeuver.unit.id, {
      ...resolution,
      isManeuver: true,
      ownerId: bestManeuver.unit.ownerId,
      unitType: bestManeuver.unit.type,
      unitSizeBeforeDamage: bestManeuver.unit.size,
      unitFactionId: bestManeuver.unit.factionId,
      unitCapturedFromOwnerId: bestManeuver.unit.capturedFromOwnerId ?? null,
    }));

    // Индикаторы на поле - для наблюдателей (не блокирует ход бота, см.
    // design.md Decision 21): blocking: false. В store.__isBotSandbox нет ни
    // сцены, ни смысла - фоновая симуляция ничего не рисует (тот же гейт,
    // что и в decideMovementStep.js)
    const renderer = store.__isBotSandbox
      ? null
      : REGISTRY_SINGLETON.getHexGridRenderer && REGISTRY_SINGLETON.getHexGridRenderer();
    if (renderer) {
      const totalDamageTaken = bestManeuver.unit.size - resolution.remainingSize;
      store.dispatch(showGoblinsTrapKillNotice({
        unit: {...bestManeuver.unit},
        died: resolution.unitDied,
        finalHexKey: resolution.finalHexKey,
        totalDamageTaken,
        triggeredTrapEvents: resolution.triggeredTrapEvents,
        blocking: false,
      }));
      // Таймера скрытия нет: индикаторы срабатывания на поле живут до конца
      // этапа (сброс по COMPLETE_PHASE/SET_WAR_PHASE_STAGE)
    }

    return true;
  }

  store.dispatch(moveUnit(bestManeuver.unit.id, bestManeuver.unit.type, bestManeuver.cellKey));
  store.dispatch(addPendingManeuverAction(
    bestManeuver.unit.id,
    bestManeuver.unit.hexKey,
    bestManeuver.cellKey,
  ));

  return true;
};
